import { Fragment, useEffect, useState, type MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { currencySymbol, type Store, type TripSummary } from '@sp/shared'
import { useSignedIn } from '../session.tsx'
import { queueTripStatus } from '../lib/tripSync.ts'
import { useStores, useTrips } from '../lib/groupData.ts'
import { useDesktop } from '../lib/useDesktop.ts'
import { invalidateResources } from '../lib/useResource.ts'
import { formatDay, itemsCount, joinNames, tripName, tripSub } from '../lib/format.ts'
import { Icon } from '../components/Icon.tsx'
import {
  AvatarStack,
  Badge,
  Button,
  Card,
  EmptyState,
  Label,
  PageTitle,
  Paragraph,
  SelectRow,
  Spinner,
  TextCol,
} from '../components/ui.tsx'
import { DeskToolbar, Panel } from '../components/desk.tsx'
import { NewTripSheet } from '../components/NewTripSheet.tsx'
import { useContextMenu } from '../components/ContextMenu.tsx'
import { useTripActions } from '../components/TripActions.tsx'

/** Seznam nakupu se obnovuje sam, at je videt, co pridal druhy telefon. */
const LIVE_MS = 30_000

/** Kolik hotovych nakupu je na pocitaci videt, nez se seznam rozbali. */
const DONE_PREVIEW = 4

/** Nejdriv nakupy, ve kterych se prave nakupuje, pak podle dne a nejnovejsi. */
function byUrgency(a: TripSummary, b: TripSummary): number {
  return (
    Number(b.status === 'shopping' && b.doneCount > 0) - Number(a.status === 'shopping' && a.doneCount > 0) ||
    (a.plannedDate ?? '9999').localeCompare(b.plannedDate ?? '9999') ||
    b.createdAt.localeCompare(a.createdAt)
  )
}

/**
 * Nakupy (navrh v3): nahore jedna karta „Teď" s jednim tlacitkem, pod ni
 * ostatni nakupy stejnym radkem bez ohledu na druh, dole hotove.
 */
export function TripsPage() {
  const s = useSignedIn()
  const navigate = useNavigate()
  const desktop = useDesktop()
  const trips = useTrips(s.group.id)
  const stores = useStores(s.group.id, true)
  const [newTrip, setNewTrip] = useState(false)
  const [showDone, setShowDone] = useState(false)
  const { onContextMenu, contextMenu } = useContextMenu()
  const { actionsFor, tripActionsSheet } = useTripActions()
  /** Prave tlacitko na nakupu: stejne akce jako v bocnim panelu. */
  const tripMenu = (trip: TripSummary) => {
    const title = tripName(trip, allStores).title
    return onContextMenu(() => actionsFor(trip, title), title)
  }
  const { reload } = trips

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') reload()
    }, LIVE_MS)
    return () => clearInterval(timer)
  }, [reload])

  const allStores = stores.res.state === 'ok' ? stores.res.data : []
  const list = trips.res.state === 'ok' ? trips.res.data : []
  const open = list.filter((t) => t.status !== 'done').sort(byUrgency)
  const [hero, ...others] = open
  const done = list
    .filter((t) => t.status === 'done')
    .sort((a, b) => (b.finishedAt ?? b.createdAt).localeCompare(a.finishedAt ?? a.createdAt))

  function shop(trip: TripSummary) {
    if (trip.status === 'planning') queueTripStatus(trip, 'shopping')
    navigate(`/nakup/${trip.id}/obchod`)
  }

  const states = (
    <>
      {trips.res.state === 'loading' && <Spinner />}
      {trips.res.state === 'error' && (
        <>
          <Paragraph tone="error">{trips.res.message}</Paragraph>
          <Button icon="refresh" className="self-start" onClick={reload}>
            Zkusit znovu
          </Button>
        </>
      )}
      {trips.res.state === 'ok' && list.length === 0 && (
        <EmptyState title="Zatím žádný nákup">
          Založ ho tlačítkem Nový nákup – stačí říct, kam jdeš. Věci pak napíšeš nebo vybereš ze šablony.
        </EmptyState>
      )}
    </>
  )

  const row = (trip: TripSummary) => {
    const name = tripName(trip, allStores)
    return (
      <SelectRow
        key={trip.id}
        as="button"
        pad="even"
        onClick={() => navigate(`/nakup/${trip.id}`)}
        onContextMenu={tripMenu(trip)}
      >
        <TextCol title={name.title} sub={tripSub(trip, allStores)} />
        {trip.status === 'shopping' && trip.doneCount > 0 && (
          <Badge kind="shopping">{`${trip.doneCount} z ${trip.itemCount}`}</Badge>
        )}
        <Icon name="chevron_right" size={20} className="tone-muted" />
      </SelectRow>
    )
  }

  const doneRow = (trip: TripSummary) => (
    <SelectRow
      key={trip.id}
      as="button"
      pad="even"
      onClick={() => navigate(`/nakup/${trip.id}`)}
      onContextMenu={tripMenu(trip)}
    >
      <TextCol
        title={tripName(trip, allStores).title}
        sub={`${formatDay(trip.finishedAt ?? trip.createdAt)} · ${itemsCount(trip.itemCount)}`}
      />
      <Icon name="chevron_right" size={20} className="tone-muted" />
    </SelectRow>
  )

  const heroCard = hero && (
    <NowCard
      onContextMenu={tripMenu(hero)}
      trip={hero}
      stores={allStores}
      names={hero.contributorIds.flatMap((id) => s.nameOf(id) ?? [])}
      wide={desktop}
      onOpen={() => navigate(`/nakup/${hero.id}`)}
      onShop={() => shop(hero)}
    />
  )

  const sheet = (
    <>
      <NewTripSheet
        open={newTrip}
        onClose={() => setNewTrip(false)}
        groupId={s.group.id}
        stores={allStores}
        onStoreAdded={() => invalidateResources(`sp:obchody:${s.group.id}:`)}
      />
      {contextMenu}
      {tripActionsSheet}
    </>
  )

  if (desktop) {
    const shownDone = showDone ? done : done.slice(0, DONE_PREVIEW)
    return (
      <div className="desk-view-main desk-narrow">
        <DeskToolbar title="Nákupy" sub={s.group.name} />
        {states}
        {heroCard}
        {(others.length > 0 || done.length > 0) && (
          <div className="desk-grid-2">
            <Panel title="Další nákupy" meta={String(others.length)}>
              {others.length > 0 ? (
                others.map(row)
              ) : (
                <Paragraph size="meta">Žádný další rozpracovaný nákup.</Paragraph>
              )}
            </Panel>
            <Panel title="Hotové nákupy" meta={String(done.length)}>
              {done.length > 0 ? (
                shownDone.map(doneRow)
              ) : (
                <Paragraph size="meta">Tady se objeví dokončené nákupy.</Paragraph>
              )}
              {done.length > DONE_PREVIEW && (
                <Button kind="quiet" compact className="self-start" onClick={() => setShowDone((v) => !v)}>
                  {showDone ? 'Zobrazit méně' : `Zobrazit všech ${done.length}`}
                </Button>
              )}
            </Panel>
          </div>
        )}
        {sheet}
      </div>
    )
  }

  return (
    <>
      {/* Bez horni listy: kdo ma vic skupin, vidi tady, ve ktere je (prepina se na zalozce Skupina). */}
      <PageTitle title="Nákupy" subtitle={s.groups.length > 1 ? s.group.name : undefined} />
      {states}
      {heroCard}

      {others.length > 0 && (
        <>
          <Label>Další nákupy</Label>
          <div className="stack-8">{others.map(row)}</div>
        </>
      )}

      {done.length > 0 && (
        <>
          <button
            type="button"
            className="disclosure"
            aria-expanded={showDone}
            onClick={() => setShowDone((v) => !v)}
          >
            <span>Hotové nákupy ({done.length})</span>
            <Icon name={showDone ? 'chevron_up' : 'chevron_down'} size={20} />
          </button>
          {showDone && <div className="stack-8">{done.map(doneRow)}</div>}
        </>
      )}

      {/* Hlavni akce stranky vpravo dole nad listou, na dosah palce. */}
      <Button kind="primary" icon="plus" className="fab" onClick={() => setNewTrip(true)}>
        Nový nákup
      </Button>
      {sheet}
    </>
  )
}

function nowMeta(trip: TripSummary, names: readonly string[]): string {
  if (trip.itemCount === 0) return 'Zatím prázdný seznam'
  if (trip.status === 'shopping') return `${trip.doneCount} z ${trip.itemCount} v košíku`
  return `${itemsCount(trip.itemCount)}${names.length > 0 ? `, ${names.length === 1 ? 'doplňuje' : 'doplňují'} ${joinNames(names)}` : ''}`
}

/** Nakup, ktery je na rade: jedna karta, jedno tlacitko. */
function NowCard({
  trip,
  stores,
  names,
  wide,
  onOpen,
  onShop,
  onContextMenu,
}: {
  onContextMenu(event: MouseEvent<HTMLElement>): void
  trip: TripSummary
  stores: readonly Store[]
  names: string[]
  wide: boolean
  onOpen(): void
  onShop(): void
}) {
  const name = tripName(trip, stores)
  const route = trip.storeIds.flatMap((id) => stores.find((x) => x.id === id) ?? [])
  const shopping = trip.status === 'shopping'
  const progress = trip.itemCount > 0 ? Math.round((trip.doneCount / trip.itemCount) * 100) : 0
  const action =
    trip.itemCount === 0 ? (
      <Button kind="primary" full={!wide} onClick={onOpen}>
        Doplnit seznam
      </Button>
    ) : (
      <Button kind="primary" full={!wide} onClick={shopping ? onOpen : onShop}>
        {shopping ? 'Pokračovat v nákupu' : 'Nakupuju'}
      </Button>
    )

  return (
    <Card className={wide ? 'trip-hero now-card' : 'trip-card now-card'} onContextMenu={onContextMenu}>
      <div className={wide ? 'trip-hero-main' : 'stack-8'}>
        <div className="row wrap-row">
          <Label as="span">Teď</Label>
          <Badge kind={shopping ? 'shopping' : 'planning'}>{shopping ? 'Nakupuje se' : 'Plánuje se'}</Badge>
        </div>
        <button type="button" className="trip-card-head" onClick={onOpen}>
          <span className="text-col">
            <span className={wide ? 'trip-hero-date' : 'trip-card-date'}>{name.title}</span>
            {name.date && <span className="meta muted">{name.date}</span>}
          </span>
          <Icon name="chevron_right" size={20} className="tone-muted" />
        </button>
        {route.length > 1 && (
          <div className="route-stops">
            {route.map((store, index) => (
              <Fragment key={store.id}>
                {index > 0 && <Icon name="arrow_right" size={16} className="tone-muted" />}
                <span className="route-stop">
                  <span className="num-badge">{index + 1}</span>
                  {store.name}
                  {store.currency !== 'CZK' && <span className="route-stop-cur">{currencySymbol(store.currency)}</span>}
                </span>
              </Fragment>
            ))}
          </div>
        )}
        {shopping && trip.itemCount > 0 && (
          <div className="now-progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
            <span style={{ width: `${progress}%` }} />
          </div>
        )}
        <div className="trip-card-meta">
          {names.length > 0 && <AvatarStack names={names} size={wide ? 28 : 24} />}
          <span>{nowMeta(trip, names)}</span>
        </div>
      </div>
      <div className={wide ? 'trip-hero-actions' : 'trip-card-actions'}>{action}</div>
    </Card>
  )
}
